import { Loader } from '@googlemaps/js-api-loader'

import { action } from '../helper'
import { Store } from '../general'
import { Country, TimeZone } from '../data'
import { Api } from '../api'
import { I18nStore } from '../i18n'

import Place from './place'

export interface GeoResult {
  title: string
  description: string
  placeId: string
}

interface Config {
  apiKey: string
}

interface RootStore {
  api: Api
  i18n: I18nStore
}

const GOOGLE_TIMEZONE_ENDPOINT = 'https://maps.googleapis.com/maps/api/timezone/json'

class GeoStore extends Store<RootStore> {
  // @ts-expect-error Google is initialized async
  private google: typeof google
  private loader: Loader
  constructor(store: RootStore, config?: Config) {
    super(store)
    this.loader = new Loader({
      apiKey: config?.apiKey || '',
      libraries: ['places'],
    })
    this.init()
  }

  async init() {
    this.google = await this.loader.load()
  }

  searchPlace = action<string, GeoResult[]>(async (query?: string) => {
    try {
      const request = {
        input: `${query}`,
        language: this.store.i18n.language,
      }
      console.debug('GEO: Search place: ', request)
      const result = await this.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
      return (result?.suggestions || []).map((suggestion) => ({
        title: suggestion.placePrediction?.mainText?.text || '',
        description: suggestion.placePrediction?.secondaryText?.text || '',
        placeId: suggestion.placePrediction?.placeId || '',
      }))
    } catch (error) {
      console.debug('GEO: Find place failed: ', error)
      throw error
    }
  })

  findPlace = action<string, Place | undefined>(async (query?: string) => {
    try {
      const request = {
        query: `${query}`,
        fields: ['name', 'formatted_address', 'place_id'],
        language: this.store.i18n.language,
      }

      console.debug('GEO: Find place: ', query)
      const results = await this.google.maps.places.Place.searchByText(request)
      const data = results?.places?.[0]
      if (!data) return

      const { id } = data
      const place = await this.getPlace(`${id}`)
      console.debug('GEO: Find place results: ', { ...place })
      return place
    } catch (error) {
      console.error('GEO: Find place failed: ', error)
      throw error
    }
  })

  getPlace = action<string, Place | undefined>(async (placeId?: string) => {
    try {
      const request = {
        fields: ['displayName', 'addressComponents', 'formattedAddress', 'location'],
      }
      console.debug('GEO: Place details : ', placeId)

      const result = await new this.google.maps.places.Place({
        id: placeId || '',
        requestedLanguage: this.store.i18n.language,
      }).fetchFields(request)

      console.debug('GEO: Place details result: ', { ...result.place })
      return result.place ? new Place(result.place) : undefined
    } catch (error) {
      console.error('GEO: Place details failed: ', error)
      throw error
    }
  })

  getCoordinates = action<
    { address: string; country?: string; postalCode?: string },
    { lat: number; lng: number } | undefined
  >(
    async (query) =>
      new Promise((resolve, reject) => {
        try {
          const geocoder = new this.google.maps.Geocoder()
          const { address, country, postalCode } = query || {}
          const request = {
            address,
            componentRestrictions: {
              country,
              postalCode,
            },
          }
          console.debug(
            `GEO: Get coordinates: ${address} ${country ? `(Country: ${country})` : ''} ${postalCode ? `(Zipcode: ${postalCode})` : ''}`,
          )
          geocoder.geocode(request, (results, status) => {
            console.debug('GEO: Coordinates (', status, '): ', results)
            if (status === 'OK') {
              const location = results?.[0]?.geometry?.location
              if (location && !results?.[0]?.partial_match) {
                const lat = location.lat()
                const lng = location.lng()
                console.debug('GEO: Coordinates: ', { lat, lng })
                resolve({ lat, lng })
              } else {
                console.debug('GEO: No location found')
                resolve(undefined)
              }
            } else {
              console.debug('GEO: Geocode failed with status: ', status)
              reject(new Error(`Geocode failed with status: ${status}`))
            }
          })
        } catch (error) {
          console.error('GEO: geocode failed: ', error)
          reject(error)
        }
      }),
    {
      mode: 'queue',
    },
  )

  getTimeZone = action<{ lat: number; lng: number } | undefined, TimeZone | undefined>(
    async (coords) => {
      if (!coords) return
      const { lat, lng } = coords
      console.debug('GEO: Get timezone: ', { lat, lng })
      const { timeZoneName: name, timeZoneId: id } = await this.store.api.get<{
        timeZoneName: string
        timeZoneId: string
      }>(
        `${GOOGLE_TIMEZONE_ENDPOINT}?location=${encodeURIComponent(`${lat},${lng}`)}&timestamp=${Date.now() / 1000}&language=${this.store.i18n.language}&key=${this.loader.apiKey}`,
      )
      console.debug('GEO: Timezone: ', name, id)
      return { name, id } as TimeZone
    },
    {
      mode: 'queue',
    },
  )

  getCountry = action<{ lat: number; lng: number } | undefined, Country | undefined>(
    async (coords) =>
      new Promise((resolve, reject) => {
        try {
          if (!coords) {
            resolve(undefined)
            return
          }
          const { lat, lng } = coords
          const geocoder = new this.google.maps.Geocoder()
          const request = {
            location: { lat, lng },
            language: this.store.i18n.language,
          }
          console.debug('GEO: Get country: ', { lat, lng })
          geocoder.geocode(request, (results, status) => {
            if (status === 'OK') {
              const country = results
                ?.map((result) => result.address_components?.find((component) => component.types?.includes('country')))
                .find((result) => !!result)
              if (country) {
                const { short_name: code, long_name: title } = country
                console.debug('GEO: Country result: ', { code, title })

                resolve({ code, title })
              } else {
                console.debug('GEO: Geocode not found: ', status)
                reject(new Error(`Geocode not found: ${status}`))
              }
            } else {
              console.debug('GEO: Geocode failed with status: ', status)
              reject(new Error(`Geocode failed with status: ${status}`))
            }
          })
        } catch (error) {
          console.error('GEO: geocode failed: ', error)
          reject(error)
        }
      }),
    {
      mode: 'queue',
    },
  )

  searchCountry = action<string, GeoResult[] | undefined>(async (query?: string) => {
    try {
      const request = {
        input: `${query}`,
        includedPrimaryTypes: ['country'],
        language: this.store.i18n.language,
      }
      console.debug('GEO: Search country: ', request)
      const result = await this.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
      return (result?.suggestions || []).map((suggestion) => ({
        title: suggestion.placePrediction?.mainText?.text || '',
        description: suggestion.placePrediction?.secondaryText?.text || '',
        placeId: suggestion.placePrediction?.placeId || '',
      }))
    } catch (error) {
      console.debug('GEO: Find country failed: ', error)
      throw error
    }
  })
}

export default GeoStore